// File: /frontend/src/services/api.js

import axios from 'axios';

// UNIVERSAL API URL - Based purely on environment variables
const getApiUrl = () => {
  // Use REACT_APP_API_URL for all environments
  return process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
};

const API_URL = getApiUrl();

console.log('🌐 Universal API URL:', API_URL);
console.log('🌐 Environment:', process.env.NODE_ENV);
console.log('🌐 REACT_APP_API_URL:', process.env.REACT_APP_API_URL || 'not set');

// Create axios instance with dynamic base URL
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true  // ✅ ENSURE THIS IS true
});

// 🔧 UNIVERSAL: Request interceptor with token handling
api.interceptors.request.use(
  (config) => {
    // Clear any invalid/conflicting tokens before each request
    const tokens = getStoredTokens();
    
    // If route is seller-specific (but not admin viewing sellers), ensure only seller token exists
    const isSellerSpecificRoute = (config.url?.startsWith('/seller/') || 
                                   (config.url?.includes('seller') && !config.url?.includes('/admin/')));
    if (isSellerSpecificRoute) {
      if (tokens.userToken || tokens.adminToken || tokens.deliveryAgentToken) {
        console.warn('🧹 Clearing conflicting tokens for seller request');
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
        localStorage.removeItem('deliveryAgentToken');
        localStorage.removeItem('deliveryAgentData');
      }
    }
    
    // Get tokens from localStorage (after cleanup)
    const userToken = localStorage.getItem('userToken');
    const sellerToken = localStorage.getItem('sellerToken');
    const adminToken = localStorage.getItem('adminToken');
    const deliveryAgentToken = localStorage.getItem('deliveryAgentToken');

    let usedToken = null;
    let tokenSource = null;
    
    // Route-based token selection
    const urlPath = config.url || '';
    const isAdminRoute = urlPath.includes('/admin/');
    const isSellerRoute = urlPath.includes('/seller/') || urlPath.startsWith('/catalogues') || urlPath.startsWith('/upload');
    const isDeliveryRoute = urlPath.includes('/delivery/');
    const isUserRoute = !isAdminRoute && !isSellerRoute && !isDeliveryRoute;

    // Priority-based token assignment
    if (isAdminRoute && adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
      usedToken = adminToken;
      tokenSource = 'adminToken';
    } else if (isSellerRoute && sellerToken) {
      config.headers.Authorization = `Bearer ${sellerToken}`;
      usedToken = sellerToken;
      tokenSource = 'sellerToken';
    } else if (isDeliveryRoute && deliveryAgentToken) {
      config.headers.Authorization = `Bearer ${deliveryAgentToken}`;
      usedToken = deliveryAgentToken;
      tokenSource = 'deliveryAgentToken';
    } else if (isUserRoute && userToken) {
      config.headers.Authorization = `Bearer ${userToken}`;
      usedToken = userToken;
      tokenSource = 'userToken';
    } else {
      // Fallback: Use any available token in priority order
      if (adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`;
        usedToken = adminToken;
        tokenSource = 'adminToken (fallback)';
      } else if (sellerToken) {
        config.headers.Authorization = `Bearer ${sellerToken}`;
        usedToken = sellerToken;
        tokenSource = 'sellerToken (fallback)';
      } else if (userToken) {
        config.headers.Authorization = `Bearer ${userToken}`;
        usedToken = userToken;
        tokenSource = 'userToken (fallback)';
      } else if (deliveryAgentToken) {
        config.headers.Authorization = `Bearer ${deliveryAgentToken}`;
        usedToken = deliveryAgentToken;
        tokenSource = 'deliveryAgentToken (fallback)';
      }
    }

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🌐 [API-REQUEST] ${config.method?.toUpperCase()} ${config.url}`);
      console.log(`🔍 [API] Route analysis:`, {
        urlPath,
        isAdminRoute,
        isSellerRoute,
        isDeliveryRoute,
        isUserRoute,
        hasAdminToken: !!adminToken,
        hasSellerToken: !!sellerToken,
        hasUserToken: !!userToken,
        hasDeliveryToken: !!deliveryAgentToken
      });
      if (usedToken) {
        console.log(`🔑 [API] Using ${tokenSource}`);
      } else {
        console.warn('⚠️ [API] No token - unauthenticated request');
        if (isAdminRoute) {
          console.error('❌ [API] Admin route detected but no admin token available!');
        }
      }
    }

    // AGGRESSIVE UPLOAD LOGGING — always log for /upload and /catalogues routes
    const isUploadRoute = urlPath.startsWith('/upload') || urlPath.startsWith('/catalogues');
    if (isUploadRoute) {
      const contentType = config.headers?.['Content-Type'] || config.headers?.['content-type'] || 'not set';
      const isFormData = config.data instanceof FormData;
      console.log(`%c[API-UPLOAD-DEBUG] ${config.method?.toUpperCase()} ${urlPath}`, 'color: #ff00ff; font-weight: bold');
      console.log(`[API-UPLOAD-DEBUG] Request config:`, {
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${urlPath}`,
        contentType,
        isFormData,
        hasAuthHeader: !!config.headers?.Authorization,
        authPreview: config.headers?.Authorization ? config.headers.Authorization.substring(0, 30) + '...' : 'NONE',
        tokenSource: tokenSource || 'NONE',
        timeout: config.timeout || api.defaults.timeout
      });
      if (isFormData) {
        // Log FormData entries
        const entries = [];
        for (const [key, val] of config.data.entries()) {
          entries.push({ key, type: val instanceof File ? `File(${val.name}, ${val.size}b, ${val.type})` : typeof val });
        }
        console.log(`[API-UPLOAD-DEBUG] FormData entries:`, entries);
      }
      if (!usedToken) {
        console.error(`%c[API-UPLOAD-DEBUG] CRITICAL: No auth token for upload/catalogue route!`, 'color: red; font-weight: bold; font-size: 16px');
      }
    }

    return config;
  },
  (error) => {
    console.error('🚨 [API] Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with enhanced error handling
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ [API-RESPONSE] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        success: response.data?.success
      });
    }
    return response;
  },
  (error) => {
    // Enhanced error logging
    const errorInfo = {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      fullURL: error.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.response?.data?.message,
      hasResponse: !!error.response,
      hasRequest: !!error.request,
      isNetworkError: !error.response && !!error.request,
      isCorsError: error.code === 'ERR_NETWORK' || (error.message && error.message.includes('CORS'))
    };
    
    console.error('🚨 [API-ERROR]:', errorInfo);

    // AGGRESSIVE UPLOAD ERROR LOGGING
    const failedUrl = error.config?.url || '';
    if (failedUrl.startsWith('/upload') || failedUrl.startsWith('/catalogues')) {
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      console.error(`%c[API-UPLOAD-ERROR] ${failedUrl} FAILED`, 'color: red; font-weight: bold; font-size: 16px; background: #ffcccc; padding: 4px 8px');
      console.error(`[API-UPLOAD-ERROR] Root cause analysis:`, {
        errorType: isTimeout ? 'TIMEOUT' : errorInfo.isNetworkError ? 'NETWORK_ERROR' : errorInfo.isCorsError ? 'CORS_ERROR' : error.response?.status === 401 ? 'AUTH_FAILED' : error.response?.status === 400 ? 'BAD_REQUEST' : error.response?.status >= 500 ? 'SERVER_ERROR' : 'UNKNOWN',
        httpStatus: error.response?.status,
        serverMessage: error.response?.data?.message,
        serverError: error.response?.data?.error,
        fullResponseBody: error.response?.data,
        axiosCode: error.code,
        axiosMessage: error.message,
        requestTimeout: error.config?.timeout
      });
    }

    // Log network/CORS errors more prominently
    if (errorInfo.isNetworkError || errorInfo.isCorsError) {
      console.error('🚨 [NETWORK/CORS ERROR]:', {
        message: error.message,
        code: error.code,
        fullURL: errorInfo.fullURL,
        suggestion: 'Check if the backend server is running and CORS is configured correctly'
      });
    }
    
    // Handle 401 errors
    if (error.response?.status === 401) {
      const errorData = error.response.data;
      const isTokenError = errorData?.code && [
        'INVALID_TOKEN',
        'TOKEN_EXPIRED', 
        'MALFORMED_TOKEN',
        'NO_TOKEN',
        'INVALID_ADMIN_TOKEN',
        'NO_ADMIN_TOKEN',
        'ADMIN_NOT_FOUND'
      ].includes(errorData.code);

      if (isTokenError) {
        console.log('🔑 [API] Token error - clearing auth data');
        
        // Clear all auth data
        ['userToken', 'userData', 'sellerToken', 'sellerData', 
         'adminToken', 'adminData', 'deliveryAgentToken', 'deliveryAgentData'].forEach(key => {
          localStorage.removeItem(key);
        });
        
        // Smart redirect based on current route
        const currentPath = window.location.pathname;
        const isCurrentlyOnLoginPage = currentPath.includes('/login');
        
        if (!isCurrentlyOnLoginPage) {
          if (currentPath.includes('/admin/')) {
            window.location.href = '/admin/login';
          } else if (currentPath.includes('/seller/')) {
            window.location.href = '/seller/login';
          } else if (currentPath.includes('/delivery/')) {
            window.location.href = '/delivery/login';
          } else {
            window.location.href = '/user/login';
          }
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Utility functions
export const getStoredTokens = () => {
  return {
    userToken: localStorage.getItem('userToken'),
    sellerToken: localStorage.getItem('sellerToken'),
    adminToken: localStorage.getItem('adminToken'),
    deliveryAgentToken: localStorage.getItem('deliveryAgentToken')
  };
};

export const clearAllTokens = () => {
  ['userToken', 'userData', 'sellerToken', 'sellerData', 
   'adminToken', 'adminData', 'deliveryAgentToken', 'deliveryAgentData'].forEach(key => {
    localStorage.removeItem(key);
  });
  console.log('🧹 [API] All tokens cleared');
};

// Debug functions in development
if (process.env.NODE_ENV === 'development') {
  window.getStoredTokens = getStoredTokens;
  window.clearAllTokens = clearAllTokens;
  window.API_URL = API_URL;
  
  // Debug admin authentication
  window.debugAdminAuth = () => {
    const adminToken = localStorage.getItem('adminToken');
    const adminData = localStorage.getItem('adminData');
    
    console.log('🔍 Admin Auth Debug:');
    console.log('  Token:', adminToken ? 'Present' : 'Missing');
    console.log('  Data:', adminData ? 'Present' : 'Missing');
    
    if (adminToken) {
      console.log('  Token length:', adminToken.length);
      console.log('  Token preview:', adminToken.substring(0, 50) + '...');
    }
    
    if (adminData) {
      try {
        const parsed = JSON.parse(adminData);
        console.log('  Admin name:', parsed.name);
        console.log('  Admin email:', parsed.email);
        console.log('  Admin role:', parsed.role);
      } catch (e) {
        console.log('  Admin data parse error:', e.message);
      }
    }
    
    return { adminToken, adminData };
  };
  
  // Test admin API endpoint
  window.testAdminAPI = async () => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      console.error('❌ No admin token found');
      return;
    }
    
    try {
      console.log('🧪 Testing admin API with token...');
      const response = await fetch('/api/admin/sellers?page=1&limit=20', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 Response status:', response.status);
      const data = await response.json();
      console.log('📦 Response data:', data);
      
      return { status: response.status, data };
    } catch (error) {
      console.error('❌ API test failed:', error);
      return { error: error.message };
    }
  };
}

export default api;